import type { AnimParams, ClipInfo } from './atlas.ts';

interface State {
  clip: ClipInfo;
  /** Clip-local time last asked for, and when (seconds of the view's clock). */
  t: number;
  at: number;
  prev: ClipInfo | null;
  prevT: number;
  fadeFrom: number;
  fade: number;
  seen: number;
  out: AnimParams;
}

/** `?anim=0` turns the clips off (the old 12 Hz poses), to compare or fall back. */
export const ANIM = { on: true };

/** Cross-fade length when a thing changes what it does (seconds). */
export const FADE = 0.14;

/**
 * Cross-fades between clips per entity: a change of clip, or a one-shot started over, blends from where the
 * old one was (still running) into the new one over `fade` seconds. Render-only state: nothing here
 * feeds back into the simulation.
 */
export class Animator {
  private st = new Map<number | string, State>();

  /**
   * What `id` plays now: `clip` at its local time `t` (seconds), at view time `now`.
   * A one-shot whose `t` goes back is a fresh start (a new swing), and blends in like a new clip.
   */
  play(id: number | string, clip: ClipInfo, t: number, now: number, fade = FADE): AnimParams {
    let s = this.st.get(id);
    if (!s) {
      s = { clip, t, at: now, prev: null, prevT: 0, fadeFrom: -1, fade, seen: now, out: { a: clip, ta: t, b: null, tb: 0, w: 0 } };
      this.st.set(id, s);
    } else if (s.clip.bones !== clip.bones) {
      // Another model with another rig (the druid turning bear): its poses don't blend with these.
      s.clip = clip;
      s.prev = null;
      s.fadeFrom = -1;
    } else {
      const dt = Math.max(0, now - s.at);
      if (s.clip !== clip || (!clip.loop && t + 0.02 < s.t)) {
        // Blend out of what it was showing, which keeps running while it fades.
        s.prev = s.clip;
        s.prevT = s.t + dt;
        s.fadeFrom = now;
        s.fade = fade;
        s.clip = clip;
      } else if (s.prev) s.prevT += dt;
    }
    s.t = t;
    s.at = now;
    s.seen = now;
    return this.params(s, now);
  }

  private params(s: State, now: number): AnimParams {
    const o = s.out;
    o.a = s.clip;
    o.ta = s.t;
    o.b = null;
    o.w = 0;
    if (s.prev && s.fadeFrom >= 0) {
      const k = (now - s.fadeFrom) / s.fade;
      if (k >= 1) s.prev = null;
      else {
        o.b = s.prev;
        o.tb = s.prevT;
        // Weight of the old clip: eased out so the new one takes over smoothly.
        const e = k < 0 ? 0 : k;
        o.w = 1 - e * e * (3 - 2 * e);
      }
    }
    return o;
  }

  /** Forgets entities not seen for `ttl` seconds (call once per tick). */
  sweep(now: number, ttl = 2): void {
    for (const [id, s] of this.st) if (now - s.seen > ttl) this.st.delete(id);
  }

  clear(): void {
    this.st.clear();
  }
}
